import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { ClientProfile } from 'shared-lib';
import { useClientQueries } from 'shared-lib';


interface ClientContextState {
  // Available properties
  properties: ClientProfile[];
  // Currently selected property
  currentProperty: ClientProfile | null;
  // Property loading state
  loading: boolean;
  // Set current property
  setCurrentProperty: (property: ClientProfile | null) => void;
  // Archive property (soft delete)
  archiveProperty: (propertyId: string) => Promise<void>;
  // Error state
  error: Error | null;
}

const ClientContext = createContext<ClientContextState | undefined>(undefined);

interface ClientContextProviderProps {
  children: ReactNode;
}

export const ClientContextProvider: React.FC<ClientContextProviderProps> = ({ children }) => {
  const [currentProperty, setCurrentProperty] = useState<ClientProfile | null>(null);
  const [error, setError] = useState<Error | null>(null);
  
  // Use the React Query hooks
  const clientQueries = useClientQueries();
  const { useClientsByUser, useUpdateClient } = clientQueries;
  
  // Get client properties
  const { 
    data: properties = [], 
    isLoading,
    refetch
  } = useClientsByUser();
  
  // Filter out archived properties
  const activeProperties = properties.filter(property => !property.isArchived);
  
  // Get update mutation
  const updateClient = useUpdateClient();
  
  // Archive property (soft delete)
  const archiveProperty = async (propertyId: string) => {
    try {
      await updateClient.mutateAsync({
        clientId: propertyId,
        data: { isArchived: true }
      });
      
      // If the archived property was the current one, select another one or null
      if (currentProperty?.id === propertyId) {
        const nextProperty = activeProperties.find(p => p.id !== propertyId) || null;
        setCurrentProperty(nextProperty);
      }
      
      // Refetch properties
      refetch();
    } catch (err) {
      setError(err as Error);
      throw err;
    }
  };
  
  // When properties load or user changes, set the default property
  useEffect(() => {
    if (!isLoading && activeProperties.length > 0 && !currentProperty) {
      setCurrentProperty(activeProperties[0]);
    }
    
    // If current property is not in the list anymore, reset it
    if (currentProperty && !activeProperties.find(p => p.id === currentProperty.id)) {
      setCurrentProperty(activeProperties[0] || null);
    }
  }, [isLoading, activeProperties, currentProperty, setCurrentProperty]);
  
  const value: ClientContextState = {
    properties: activeProperties,
    currentProperty,
    loading: isLoading,
    setCurrentProperty,
    archiveProperty,
    error,
  };
  
  return (
    <ClientContext.Provider value={value}>
      {children}
    </ClientContext.Provider>
  );
};

// Hook for using the client context
export const useClientContext = (): ClientContextState => {
  const context = useContext(ClientContext);
  if (context === undefined) {
    throw new Error('useClientContext must be used within a ClientContextProvider');
  }
  return context;
}; 